import _ from 'lodash';
import { billingCycleDependBasePlanTypeList, limitBillingCycleWithBaseZoneTypes, BillingCycleEnum } from '@/common/constants';

/**
 * Check if a base plan can switch to target billing cycle
 * by verifying if other base plans can support all dependent addons after the switch
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.currentSubscriptions - Current subscriptions in cart (getUserCurrentSubscriptions)
 * @param {string} params.currentBasePlanType - Current base plan type to exclude (e.g., planTypes.ZOOM_MEETING)
 * @param {string} params.targetBillingCycle - Target billing cycle to switch to (e.g., BILLING_CYCLE.MONTHLY)
 * @returns {boolean} - True if can switch, false otherwise
 */
export function canSwitchBillingCycle({ currentSubscriptions, currentBasePlanType, targetBillingCycle }) {
  // Get all base plans in cart (excluding current base plan to simulate the switch)
  const allBasePlans = _.filter(currentSubscriptions, subscription =>
    _.includes(billingCycleDependBasePlanTypeList, subscription.type) &&
    subscription.type !== currentBasePlanType
  );

  if (_.isEmpty(allBasePlans)) {
    return false;
  }

  const basePlanTypes = _.map(allBasePlans, 'type');

  // Get addons that depend on any base plan (including current base plan)
  const dependentAddons = _.filter(currentSubscriptions, subscription => {
    if (!_.includes(limitBillingCycleWithBaseZoneTypes, subscription.type)) {
      return false;
    }
    const dependList = _.get(subscription, 'dependBasePlanTypeList', []);
    // Check if addon depends on any base plan (including current base plan)
    return _.some(dependList, dependType =>
      _.includes(basePlanTypes, dependType) || dependType === currentBasePlanType
    );
  });

  if (_.isEmpty(dependentAddons)) {
    return false;
  }

  // Get max cycleValue of dependent addons
  const maxAddonCycleValue = _.maxBy(dependentAddons, 'cycleValue')?.cycleValue || 0;

  // Get target billing cycle's cycleValue
  const targetCycleValue = BillingCycleEnum[targetBillingCycle] || 0;

  // Simulate: if current base plan switches to target cycle, consider both target cycle and other base plans
  const maxBaseCycleValue = _.maxBy(allBasePlans, 'cycleValue')?.cycleValue || 0;
  const maxCycleAfterSwitch = Math.max(targetCycleValue, maxBaseCycleValue);

  // Allow switch if the max cycle after switch >= addon's max cycle
  return maxCycleAfterSwitch >= maxAddonCycleValue;
}
